import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import { useApp } from '../context/AppContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { MapPin, Phone, Mail, Clock, Star, ArrowLeft, AlertCircle, CreditCard, Trash2, MessageSquare, CheckCircle, ShieldAlert, Eye } from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const ServiceDetailPage = () => {
  const { serviceId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isAuthenticated } = useApp();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const fetchService = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/services/${serviceId}`);
      if (res.ok) {
        const data = await res.json();
        setService(data);
      } else {
        toast.error('Service not found');
        navigate('/services');
      }
    } catch {
      toast.error('Failed to load service');
    } finally {
      setLoading(false);
    }
  }, [serviceId, navigate]);

  useEffect(() => {
    fetchService();
  }, [fetchService]);

  // Handle subscription return from Stripe
  useEffect(() => {
    const subStatus = searchParams.get('subscription');
    const sessionId = searchParams.get('session_id');
    if (subStatus === 'success' && sessionId) {
      pollSubscriptionStatus(sessionId);
    } else if (subStatus === 'cancelled') {
      toast.info('Subscription setup was cancelled.');
    }
  }, [searchParams]);

  const pollSubscriptionStatus = async (sessionId, attempts = 0) => {
    if (attempts >= 20) {
      setVerifying(false);
      toast.error('Subscription verification timed out. Please refresh.');
      return;
    }
    setVerifying(true);
    try {
      const res = await fetch(`${API_BASE}/api/services/${serviceId}/subscription-status/${sessionId}`);
      const data = await res.json();
      if (data.status === 'active') {
        toast.success('Subscription active! Your listing is live.');
        setVerifying(false);
        fetchService();
        return;
      }
      setTimeout(() => pollSubscriptionStatus(sessionId, attempts + 1), 2000);
    } catch {
      setTimeout(() => pollSubscriptionStatus(sessionId, attempts + 1), 2000);
    }
  };

  const handleSubscribe = async () => {
    setPaymentLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/services/${serviceId}/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ frontend_url: window.location.origin })
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        toast.error(data.detail || 'Failed to create subscription session');
      }
    } catch {
      toast.error('Payment error. Please try again.');
    } finally {
      setPaymentLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this service listing?')) return;
    try {
      const res = await fetch(`${API_BASE}/api/services/${serviceId}`, { method: 'DELETE' });
      if (res.ok) {
        toast.success('Service deleted');
        navigate('/services');
      }
    } catch {
      toast.error('Failed to delete');
    }
  };

  const handleContact = () => {
    if (service.contact_phone) {
      window.open(`tel:${service.contact_phone}`);
    } else if (service.contact_email) {
      window.open(`mailto:${service.contact_email}`);
    } else {
      toast.info('No contact info available');
    }
  };

  if (loading) {
    return (
      <Layout showBack>
        <div className="px-4 py-8 space-y-4 animate-pulse">
          <div className="h-6 bg-stone-100 rounded w-3/4" />
          <div className="h-4 bg-stone-100 rounded w-1/2" />
          <div className="h-20 bg-stone-100 rounded" />
        </div>
      </Layout>
    );
  }

  if (!service) return null;

  const isOwner = user?.id === service.provider_id;
  const freeUntil = service.free_until ? new Date(service.free_until) : null;
  const isFreeActive = freeUntil && freeUntil > new Date();
  const subStatus = service.subscription_status || 'trialing';
  const freeExpired = freeUntil && freeUntil <= new Date() && subStatus === 'trialing';
  const needsPayment = service.payment_required || service.status === 'payment_due' || freeExpired;
  const isGracePeriod = service.status === 'grace_period';
  const isExpired = service.status === 'expired';
  const isActive = subStatus === 'active' || (subStatus === 'trialing' && isFreeActive);

  return (
    <Layout showBack>
      <div className="px-4 py-4 space-y-4">
        {/* Verifying banner */}
        {verifying && (
          <div className="bg-blue-50 border border-blue-200 rounded-sm p-3 text-center">
            <p className="text-sm text-blue-700 animate-pulse">Verifying subscription...</p>
          </div>
        )}

        {/* Header */}
        <div>
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1">
              <h1 className="font-display text-xl font-semibold text-stone-900" data-testid="service-title">
                {service.title}
              </h1>
              <p className="text-sm text-stone-500 mt-0.5">{service.provider_name}</p>
            </div>
            <Badge className="bg-minted-50 text-minted-700 text-[10px] rounded-sm border-0 tracking-wider uppercase shrink-0">
              {service.category}
            </Badge>
          </div>

          {/* Status badges */}
          <div className="flex flex-wrap gap-2 mt-3">
            {isFreeActive && (
              <span className="text-[10px] bg-emerald-50 text-emerald-600 px-2 py-1 rounded-sm font-medium" data-testid="free-badge">
                Free until {freeUntil.toLocaleDateString()}
              </span>
            )}
            {subStatus === 'active' && !isFreeActive && (
              <span className="text-[10px] bg-emerald-50 text-emerald-600 px-2 py-1 rounded-sm font-medium flex items-center gap-1" data-testid="active-badge">
                <CheckCircle className="w-3 h-3" /> Subscription Active
              </span>
            )}
            {isGracePeriod && (
              <span className="text-[10px] bg-amber-50 text-amber-600 px-2 py-1 rounded-sm font-medium flex items-center gap-1" data-testid="grace-badge">
                <ShieldAlert className="w-3 h-3" /> Grace Period — {service.grace_days_left || 0} days left
              </span>
            )}
            {isExpired && (
              <span className="text-[10px] bg-red-50 text-red-600 px-2 py-1 rounded-sm font-medium" data-testid="expired-badge">
                Listing Expired
              </span>
            )}
            {needsPayment && isOwner && !isGracePeriod && !isExpired && (
              <span className="text-[10px] bg-red-50 text-red-600 px-2 py-1 rounded-sm font-medium" data-testid="payment-due-badge">
                Payment due
              </span>
            )}
          </div>
        </div>

        {/* Payment Required Banner (for owner only) */}
        {isOwner && (needsPayment || isGracePeriod || isExpired) && (
          <div className={`border rounded-sm p-4 ${isExpired ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`} data-testid="payment-banner">
            <div className="flex items-start gap-3">
              <AlertCircle className={`w-5 h-5 shrink-0 mt-0.5 ${isExpired ? 'text-red-600' : 'text-amber-600'}`} />
              <div className="flex-1">
                <p className={`text-sm font-medium ${isExpired ? 'text-red-800' : 'text-amber-800'}`}>
                  {isExpired
                    ? 'Your listing has been removed'
                    : isGracePeriod
                    ? 'Payment failed — update your payment method'
                    : 'Your free week has ended'}
                </p>
                <p className={`text-xs mt-1 ${isExpired ? 'text-red-600' : 'text-amber-600'}`}>
                  {isExpired
                    ? 'Subscribe to reactivate your listing ($1/week, auto-renewing).'
                    : isGracePeriod
                    ? `You have ${service.grace_days_left || 0} days to update payment before your listing is removed.`
                    : 'Subscribe for $1/week to keep your listing active (auto-renewing).'}
                </p>
                <Button
                  className={`mt-3 text-white text-xs h-8 ${isExpired ? 'bg-red-600 hover:bg-red-700' : 'bg-amber-600 hover:bg-amber-700'}`}
                  onClick={handleSubscribe}
                  disabled={paymentLoading}
                  data-testid="subscribe-service-btn"
                >
                  <CreditCard className="w-3.5 h-3.5 mr-1.5" />
                  {paymentLoading ? 'Processing...' : 'Subscribe — $1/week'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Description */}
        {service.description && (
          <div>
            <h3 className="text-xs font-medium text-stone-400 uppercase tracking-wider mb-1.5">About</h3>
            <p className="text-sm text-stone-700 leading-relaxed" data-testid="service-description">
              {service.description}
            </p>
          </div>
        )}

        {/* Price Info */}
        {service.price_info && (
          <div>
            <h3 className="text-xs font-medium text-stone-400 uppercase tracking-wider mb-1.5">Pricing</h3>
            <p className="text-sm text-stone-700">{service.price_info}</p>
          </div>
        )}

        {/* Contact Details */}
        <div className="bg-white border border-stone-100 rounded-sm p-4 space-y-3">
          <h3 className="text-xs font-medium text-stone-400 uppercase tracking-wider">Contact</h3>
          {service.location && (
            <div className="flex items-center gap-2 text-sm text-stone-600">
              <MapPin className="w-4 h-4 text-stone-400" strokeWidth={1.5} />
              {service.location}
            </div>
          )}
          {service.contact_phone && (
            <a href={`tel:${service.contact_phone}`} className="flex items-center gap-2 text-sm text-minted-600 hover:text-minted-700">
              <Phone className="w-4 h-4" strokeWidth={1.5} />
              {service.contact_phone}
            </a>
          )}
          {service.contact_email && (
            <a href={`mailto:${service.contact_email}`} className="flex items-center gap-2 text-sm text-minted-600 hover:text-minted-700">
              <Mail className="w-4 h-4" strokeWidth={1.5} />
              {service.contact_email}
            </a>
          )}
        </div>

        {/* Actions */}
        <div className="space-y-2 pt-2">
          {!isOwner && (
            <Button
              className="w-full bg-minted-900 hover:bg-minted-700 rounded-sm h-11"
              onClick={handleContact}
              data-testid="contact-service-btn"
            >
              <MessageSquare className="w-4 h-4 mr-2" strokeWidth={1.5} />
              Contact Provider
            </Button>
          )}
          
          {isOwner && !needsPayment && !isExpired && (
            <Button
              variant="outline"
              className="w-full border-red-200 text-red-600 hover:bg-red-50 rounded-sm h-10"
              onClick={handleDelete}
              data-testid="delete-service-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" strokeWidth={1.5} />
              Delete Listing
            </Button>
          )}
        </div>

        {/* Listing info */}
        <div className="text-center pt-2 pb-4">
          <p className="text-[10px] text-stone-400">
            Listed {new Date(service.created_at).toLocaleDateString()}
            {' · '}
            <Eye className="w-3 h-3 inline-block align-text-bottom" strokeWidth={1.5} />
            {' '}{service.views || 0} views
          </p>
        </div>
      </div>
    </Layout>
  );
};

export default ServiceDetailPage;
